/**
 * Admin Course API client (Sprint 8A B2).
 *
 * Mirror BE endpoint `/api/admin/courses/` từ apps.catalog.admin_views.
 */
import { apiClient } from "@/api/client";

// Sprint 26 — auto-freeze Major versioning. Mirror `curriculum.ts::readForkedToMajorId`
// (không import chéo để tránh circular giữa 2 module type-only import lẫn nhau).
function readForkedToMajorId(headers: unknown): number | null {
  const raw = (headers as Record<string, string> | undefined)?.["x-major-forked-to"];
  return raw ? Number(raw) : null;
}

export interface Forkable {
  forkedToMajorId: number | null;
}

export interface AdminTag {
  id: number;
  name: string;
  slug: string;
}

export interface CourseRef {
  id: number;
  code: string;
  name: string;
}

// Sprint 26+ — flat = học phí cố định (price_cents); per_credit = học phí =
// TuitionRate hiện hành của tenant × credits (xem admin/api/tuitionRates.ts).
export type PricingMode = "flat" | "per_credit";

// TT 83 C1-b — hình thức tổ chức học phần ("" = chưa khai báo).
export type DeliveryFormat = "truc_tiep" | "truc_tuyen" | "ket_hop";

/** Ngôn ngữ câu hỏi Phát âm — khớp `catalog.SpeakingLanguage` BE. */
export type SpeakingLanguage = "ja" | "en" | "zh" | "ko";

export interface AdminCourseListItem {
  id: number;
  code: string;
  slug: string;
  name: string;
  credits: number;
  price_cents: number;
  pricing_mode: PricingMode;
  delivery_format: DeliveryFormat | "";
  online_ratio_pct: number | null;
  /** Ngôn ngữ câu hỏi Phát âm (plan 2026-10-03). "" = môn không có câu Phát âm. */
  speaking_language?: SpeakingLanguage | "";
  cover_image: string | null;
  is_published: boolean;
  tags: AdminTag[];
  lesson_count: number;
  term_names: string[];
  pre_course_ids: number[];
  prerequisite_ids: number[];
  // Chốt 2026-07-13 — giảng viên ĐANG phụ trách (null = chưa phân công). Gán/
  // đổi/gỡ qua `admin/api/courseInstructorAssignments.ts`, KHÔNG qua PATCH.
  current_instructor: number | null;
  current_instructor_email: string | null;
  created_at: string;
  updated_at: string;
}

export interface AdminCourseDetail extends AdminCourseListItem {
  description: string;
  /** Kỳ giảng viên ĐỀ XUẤT khi publish qua AI Lesson Designer — môn CHƯA gắn vào
   *  kỳ này; quản trị trường duyệt bằng cách gắn ở tab "Kỳ học" (2026-10-01). */
  proposed_term?: {
    id: number;
    name: string;
    major_name: string;
    proposed_by: string;
  } | null;
  pre_courses: CourseRef[];
  prerequisites: CourseRef[];
}

export interface AdminCourseListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: AdminCourseListItem[];
}

export interface AdminTermOption {
  id: number;
  slug: string;
  name: string;
  order: number;
  major: number;
  major_name: string;
  program_name: string;
}

export interface AdminCourseListParams {
  page?: number;
  is_published?: boolean;
  terms__major__slug?: string;
  search?: string;
  ordering?: string;
}

export interface AdminCourseCreatePayload {
  code: string;
  name: string;
  description?: string;
  credits?: number;
  price_cents?: number;
  pricing_mode?: PricingMode;
  delivery_format?: DeliveryFormat | "";
  online_ratio_pct?: number | null;
  speaking_language?: SpeakingLanguage | "";
  tag_ids?: number[];
  is_published?: boolean;
  pre_course_ids?: number[];
  prerequisite_ids?: number[];
}

export type AdminCourseUpdatePayload = Partial<AdminCourseCreatePayload>;

export interface AdminMyPermissions {
  role: string;
  actions: string[];
}

const COURSES = "/admin/courses/";

export async function listAdminCourses(
  params: AdminCourseListParams = {},
): Promise<AdminCourseListResponse> {
  const res = await apiClient.get<AdminCourseListResponse>(COURSES, { params });
  return res.data;
}

export async function getAdminCourse(slug: string): Promise<AdminCourseDetail> {
  const res = await apiClient.get<AdminCourseDetail>(`${COURSES}${slug}/`);
  return res.data;
}

export async function createAdminCourse(
  payload: AdminCourseCreatePayload,
): Promise<AdminCourseDetail> {
  const res = await apiClient.post<AdminCourseDetail>(COURSES, payload);
  return res.data;
}

export async function updateAdminCourse(
  slug: string,
  payload: AdminCourseUpdatePayload,
): Promise<AdminCourseDetail> {
  const res = await apiClient.patch<AdminCourseDetail>(`${COURSES}${slug}/`, payload);
  return res.data;
}

export async function deleteAdminCourse(slug: string): Promise<void> {
  await apiClient.delete(`${COURSES}${slug}/`);
}

export async function publishAdminCourse(slug: string): Promise<AdminCourseDetail> {
  const res = await apiClient.post<AdminCourseDetail>(`${COURSES}${slug}/publish/`);
  return res.data;
}

export async function unpublishAdminCourse(slug: string): Promise<AdminCourseDetail> {
  const res = await apiClient.post<AdminCourseDetail>(`${COURSES}${slug}/unpublish/`);
  return res.data;
}

export async function uploadCoverImage(slug: string, file: File): Promise<AdminCourseDetail> {
  /** Multipart upload chỉ cover_image — sử dụng PATCH với FormData. */
  const fd = new FormData();
  fd.append("cover_image", file);
  const res = await apiClient.patch<AdminCourseDetail>(`${COURSES}${slug}/`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
}

// ─── Gói nội dung F-Future (.zip) — plan 2026-10-27 ────────────────────────
// BE: POST /api/admin/courses/<slug>/content-pack/ (apps.catalog.admin_views.content_pack).

export interface ContentPackSummary {
  course_code?: string;
  module?: string;
  lessons?: { order: number; lesson_id: string; title: string }[];
  quizzes?: { title: string; kind: "assignment" | "exam" }[];
  media_count?: number;
  /** Tiêu đề bài SCORM cũ mà gói thay thế ("" = không thay bài nào). */
  replaces_scorm?: string;
}

export interface ContentPackReport {
  dry_run: boolean;
  course: string;
  module: string;
  lessons_created: number;
  lessons_updated: number;
  lessons_skipped: number;
  quizzes_created: number;
  quizzes_updated: number;
  quizzes_rebuilt: number;
  quizzes_skipped_attempts: number;
  audio_new: number;
  audio_existing: number;
  images_new: number;
  videos_attached: number;
  videos_unchanged: number;
  scorm_removed: number;
  scorm_archived: number;
  warnings: string[];
}

export interface ContentPackResult {
  dry_run: boolean;
  summary: ContentPackSummary;
  report: ContentPackReport;
}

/** Body lỗi 400 — gói sai định dạng (`errors`) hoặc không nạp được (`detail`). */
export interface ContentPackErrorBody {
  detail: string;
  errors?: string[];
  warnings?: string[];
  summary?: ContentPackSummary;
}

export const MAX_CONTENT_PACK_BYTES = 25 * 1024 * 1024;

export async function importContentPack(
  slug: string,
  file: File,
  opts: {
    dryRun: boolean;
    rebuild?: boolean;
    replaceScorm?: boolean;
    onUploadProgress?: (e: { loaded: number; total?: number }) => void;
  },
): Promise<ContentPackResult> {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("dry_run", String(opts.dryRun));
  fd.append("rebuild", String(!!opts.rebuild));
  fd.append("replace_scorm", String(!!opts.replaceScorm));
  const res = await apiClient.post<ContentPackResult>(`${COURSES}${slug}/content-pack/`, fd, {
    onUploadProgress: opts.onUploadProgress,
  });
  return res.data;
}

// ─── Tag + Term lookup ─────────────────────────────────────────────────────

export async function listAdminTags(search?: string): Promise<AdminTag[]> {
  const res = await apiClient.get<{ results: AdminTag[] } | AdminTag[]>("/admin/tags/", {
    params: search ? { search } : {},
  });
  // DRF pagination wraps → results
  return Array.isArray(res.data) ? res.data : res.data.results;
}

export async function listAdminTerms(): Promise<AdminTermOption[]> {
  const res = await apiClient.get<{ results: AdminTermOption[] } | AdminTermOption[]>(
    "/admin/terms/",
  );
  return Array.isArray(res.data) ? res.data : res.data.results;
}

// ─── Term ↔ Course link (Sprint 26 B) ───────────────────────────────────────
// Course tạo độc lập khỏi Term — gắn/gỡ term sau khi course đã tồn tại qua đây.

export interface AdminTermCourseLink {
  id: number;
  term: number;
  course: number;
  order: number;
  // Sprint 26 C — môn bắt buộc hay tự chọn trong kỳ này (đối chiếu tín chỉ Major).
  is_required: boolean;
  term_name: string;
  course_name: string;
  // Sprint 26 — set khi request này gây auto-freeze Major cha (= id Major BỊ freeze).
  forked_from_major_id: number | null;
}

export async function listCourseTerms(courseId: number): Promise<AdminTermCourseLink[]> {
  const res = await apiClient.get<{ results: AdminTermCourseLink[] } | AdminTermCourseLink[]>(
    "/admin/term-courses/",
    { params: { course: courseId } },
  );
  return Array.isArray(res.data) ? res.data : res.data.results;
}

export async function linkCourseTerm(
  courseId: number,
  termId: number,
  isRequired = true,
  order = 1,
): Promise<AdminTermCourseLink & Forkable> {
  const res = await apiClient.post<AdminTermCourseLink>("/admin/term-courses/", {
    course: courseId,
    term: termId,
    is_required: isRequired,
    order,
  });
  return { ...res.data, forkedToMajorId: readForkedToMajorId(res.headers) };
}

export async function unlinkCourseTerm(linkId: number): Promise<Forkable> {
  const res = await apiClient.delete(`/admin/term-courses/${linkId}/`);
  return { forkedToMajorId: readForkedToMajorId(res.headers) };
}

/** Lấy tất cả TermCourse của các kỳ trong major có order < termOrder.
 *  Dùng để kiểm tra môn học trước / tiên quyết đã xuất hiện trong kỳ trước chưa. */
export async function listPrevTermCourses(
  majorId: number,
  termOrder: number,
): Promise<AdminTermCourseLink[]> {
  const res = await apiClient.get<{ results: AdminTermCourseLink[] } | AdminTermCourseLink[]>(
    "/admin/term-courses/",
    { params: { term__major: majorId, term__order__lt: termOrder } },
  );
  return Array.isArray(res.data) ? res.data : res.data.results;
}

/** Lấy TOÀN BỘ TermCourse của mọi kỳ trong 1 major (không lọc theo order) —
 *  dùng để audit môn học trước/tiên quyết ngay khi vào trang chi tiết ngành
 *  học, không đợi tới lúc admin thao tác gắn thêm môn (xem `MajorDetail.tsx`). */
export async function listAllTermCoursesForMajor(majorId: number): Promise<AdminTermCourseLink[]> {
  const res = await apiClient.get<{ results: AdminTermCourseLink[] } | AdminTermCourseLink[]>(
    "/admin/term-courses/",
    { params: { term__major: majorId } },
  );
  return Array.isArray(res.data) ? res.data : res.data.results;
}

// ─── Permissions helper ────────────────────────────────────────────────────

export async function getMyPermissions(): Promise<AdminMyPermissions> {
  const res = await apiClient.get<AdminMyPermissions>("/admin/courses/me/permissions/");
  return res.data;
}
